// The Materials tab (docs/screens/job-view-screens-round7-materials.html frame f1, with the in-margin column named
// "Price"; the sheet list and the head from round 5 f3 and f3b; brief section 5). A review tool, not a step: what this
// build buys, what each line charges, and which lines sit outside the margin. By material (one line per material across
// the job, grouped by the material's category, typed prices first) or by kind of item.
//
// Separate pricing and Ignore margin (Spencer, 2026-10-22): every material line carries the Separate pricing box;
// checked, its markup (the company's until typed over, gold when typed) and the Ignore margin box appear; Ignore margin
// checked moves the line's price to "Price outside margin". The flags live on each item's BOM line: on an item built from
// a catalog item an item-only change in its custom_bom naming the recipe line, on a custom item's own line that line.
// The pooled row's box sets every item line of that material at once; an item line's box sets that line alone. A job
// BOM line has no home for the flags and shows none. Sales tax is the company rate on every line's cost, in each
// column's Materials total. The foot's in-margin total is the Price tab's Materials row (with the margin applied
// there); the outside total is its Materials priced outside margin row.

import { Fragment, useState, type ReactNode } from 'react';
import { jobTier, materialsByMaterial, type MaterialLine, type ResolvedBom, type ResolvedGroup, type ResolvedItem, type Tier } from '@zerogap/engine';
import { toast, updateRow, useRows, type Row } from '../../../lib/api';
import { money, qty } from '../../../lib/format';
import { pullEarly, pullOpen, undoPull, useInventory, type InventoryLine } from '../../../lib/inventory';
import { ensureStored } from '../../../lib/job';
import { useSession } from '../../../lib/session';
import { Btn, Chain, Info, Money, Tag, useFolds } from '../../../ui/kit';
import { bomPackWords } from '../../../ui/pack';
import { groupAddress, materialAddress, UnverifiedTag } from '../../../ui/unverified';
import { useInventoryNames } from '../../inventory/names';
import { Wide } from '../../../ui/wide';
import type { JobCtx } from '../Workspace';
import { allocate, byNumber, Jump, LibraryJump, TabHead, Tot, useChoice } from './common';

type MaterialView = 'material' | 'item';
const VIEWS = ['material', 'item'] as const;

/** A line's pricing as stored where it lives: the item's own line when it carries any flag, else the recipe line. */
interface Pricing {
  sep: boolean;
  /** The markup typed on the line, as text; null = the company materials markup. */
  markup: string | null;
  ignore: boolean;
}

/** One item's share of one BOM line: what an item line's boxes set. */
interface ItemLine {
  key: string;
  g: ResolvedGroup;
  b: ResolvedBom;
  item: ResolvedItem;
  cost: number;
  price: number;
  /** Nothing of the line on this item (a drop priced at nothing): not listed, but set with the rest. */
  zero: boolean;
}

/** A row of the materials table: one material across the job (by material), or one line of one kind of item. */
interface Model {
  key: string;
  line: MaterialLine;
  boms: { g: ResolvedGroup; b: ResolvedBom }[];
  items: ItemLine[];
  jobLines: ResolvedBom[];
  priceIn: number;
  priceOut: number;
}

const poolKey = (b: ResolvedBom) => b.material_id ?? `${b.material_role ?? ''}|${b.description}`;
const pctText = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));
const hasPricing = (e: Row | undefined) => !!e && (e.separate_pricing !== undefined || e.ignore_margin !== undefined || (e.markup_pct !== undefined && e.markup_pct !== null && String(e.markup_pct) !== ''));
const samePricing = (a: Pricing, b: Pricing) => a.sep === b.sep && a.ignore === b.ignore && (a.markup === null ? b.markup === null : b.markup !== null && Number(a.markup) === Number(b.markup));

/** What a material row on the job says about purchasing, and the early pull once the job is won. */
function Pull({ tier, line, allowed }: { tier: Tier; line: InventoryLine | undefined; allowed: boolean }) {
  const names = useInventoryNames();
  if (tier === 'firm') return <Tag kind="off">in {names.purchasing}</Tag>;
  // before Won nothing reaches purchasing; a line is missing for materials nobody counts
  if (tier !== 'committed' || !line) return null;
  const more = Number(line.buy) > 0;
  const on = [...new Set(line.ordered.map((o) => o.order.po.number as string))];
  return (
    <>
      {line.pulled && <Tag kind="on">pulled early{on.length > 0 ? ` · on ${on.join(', ')}` : ''}</Tag>}{' '}
      {allowed && line.rows.some(pullOpen) && (
        <button type="button" className="linklike" onClick={() => void undoPull(line)}>
          Undo
        </button>
      )}{' '}
      {allowed && more && (
        <Btn small onClick={() => void pullEarly(line)}>
          {line.pulled ? `Pull ${qty(line.buy)} more` : `Pull to ${names.purchasing} now`}
        </Btn>
      )}
      {!line.pulled && !more && <small className="muted">covered from stock</small>}
    </>
  );
}

/** The designed checkbox: on, off, or mixed when the lines under a pooled row differ. */
function Box({ on, mixed, label, disabled, onClick }: { on: boolean; mixed?: boolean; label: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? 'mixed' : on}
      aria-label={label}
      title={mixed ? `${label}: mixed, the lines differ. Click to set every line.` : label}
      className={`cb${on ? ' on' : ''}${mixed ? ' mix' : ''}`}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    />
  );
}

/** The markup on a separately priced line: typed over (gold) or the company's ("std"), or mixed under a pooled row. */
function Markup({ used, typed, mixed, label, disabled, onCommit }: { used: string; typed: boolean; mixed: boolean; label: string; disabled?: boolean; onCommit: (v: string | null) => void }) {
  const shown = mixed ? '' : pctText(used);
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const t = draft.trim().replace(/%$/, '').trim();
    setDraft(null);
    if (t === shown && (typed || mixed)) return;
    if (t === '') return onCommit(null);
    if (!/^-?\d+(\.\d+)?$/.test(t)) return void toast('Type the markup as a number of percent, like 20.');
    if (!mixed && typed && Number(t) === Number(used)) return;
    onCommit(t);
  };
  return (
    <span className="bd-mk">
      <span className={`in num${typed && !mixed ? ' ov' : ''}`} onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          inputMode="decimal"
          aria-label={label}
          value={draft ?? shown}
          placeholder={mixed ? 'mixed' : ''}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setDraft(null);
          }}
        />
        {!mixed && <span>%</span>}
      </span>
      {!typed && !mixed && <span className="std">std</span>}
    </span>
  );
}

export function MaterialsTab({ ctx }: { ctx: JobCtx }) {
  const { can } = useSession();
  const res = ctx.r.res!;
  const lib = ctx.r.lib!;
  const jobTypeId = (ctx.data.job?.job_type_id as string | null) ?? null;
  const [view, setView] = useChoice<MaterialView>(`job.${ctx.jobId}.materials`, 'material', VIEWS);
  const folds = useFolds(`job.${ctx.jobId}.materials.${view}`, true);
  const lineFolds = useFolds(`job.${ctx.jobId}.materials.lines`);
  const [busy, setBusy] = useState(false);
  const revisions = useRows('revision', { eq: { job_id: ctx.jobId } });
  // how sure this job's quantities are decides what purchasing sees of it
  const tier = jobTier({ has_revision: revisions.rows.length > 0, outcome: ctx.data.opportunity?.outcome });
  // the same lines Purchasing shows, so pulling here and pulling there are one action
  const inv = useInventory();
  const names = useInventoryNames();
  const invLine = (materialId: string | null) => inv.lines.find((l) => l.job_id === ctx.jobId && l.material_id === materialId);
  const seePrices = can('see_prices');
  const libraryOpen = can('library.open_from_job');
  const canPull = can('screen.purchasing');
  const itemsById = new Map(res.items.map((i) => [i.id, i]));
  const rowOf = (id: string) => ctx.data.items.find((r) => r.id === id);

  // ---------- where a line's flags live, and what they are ----------
  const recipeLine = (item: ResolvedItem, b: ResolvedBom) => (item.catalog_item_id && b.line_id ? lib.catalog[item.catalog_item_id]?.recipes.find((r) => r.id === item.recipe_id)?.bom.find((l) => l.id === b.line_id) : undefined);
  /** The index of this line's entry in the item's custom_bom: -1 when a recipe line has none yet. */
  const entryIndex = (b: ResolvedBom, own: Row[]): number => {
    if (b.own_index !== undefined && b.own_index !== null) return b.own_index;
    if (b.source === 'recipe' && b.line_id) return own.findIndex((e) => e.line_id === b.line_id);
    const m = /#b(\d+)$/.exec(b.key);
    return m ? Number(m[1]) : -1;
  };
  const fromSource = (e: { separate_pricing?: boolean | null; markup_pct?: unknown; ignore_margin?: boolean | null } | undefined): Pricing => {
    const sep = !!e?.separate_pricing;
    const m = e?.markup_pct;
    return { sep, markup: sep && m !== undefined && m !== null && String(m).trim() !== '' ? String(m) : null, ignore: sep && !!e?.ignore_margin };
  };
  const pricingOf = (item: ResolvedItem, b: ResolvedBom): Pricing => {
    const own = (rowOf(item.id)?.custom_bom ?? []) as Row[];
    const k = entryIndex(b, own);
    const entry = k >= 0 ? own[k] : undefined;
    if (b.source === 'recipe' && b.line_id && !hasPricing(entry)) return fromSource(recipeLine(item, b) as any);
    return fromSource(entry as any);
  };

  /** Sets the flags on item lines: each item's custom_bom written once. */
  const write = async (lines: ItemLine[], change: Partial<Pricing>) => {
    if (ctx.locked || lines.length === 0) return;
    setBusy(true);
    try {
      const byItem = new Map<string, ItemLine[]>();
      for (const l of lines) byItem.set(l.item.id, [...(byItem.get(l.item.id) ?? []), l]);
      for (const [, ls] of byItem) {
        const item = ls[0]!.item;
        const id = await ensureStored(item, ctx.jobId, res.items);
        const own = ((rowOf(id)?.custom_bom ?? []) as Row[]).map((e) => ({ ...e }));
        const drop = new Set<number>();
        for (const l of ls) {
          const cur = pricingOf(item, l.b);
          const next: Pricing = { sep: change.sep ?? cur.sep, markup: 'markup' in change ? change.markup ?? null : cur.markup, ignore: change.ignore ?? cur.ignore };
          if (!next.sep) {
            next.markup = null;
            next.ignore = false;
          }
          let k = entryIndex(l.b, own);
          const onRecipe = l.b.source === 'recipe' && !!l.b.line_id;
          if (onRecipe && samePricing(next, fromSource(recipeLine(item, l.b) as any))) {
            // back to what the catalog says: no item-only pricing left on this line
            if (k >= 0) {
              delete own[k]!.separate_pricing;
              delete own[k]!.markup_pct;
              delete own[k]!.ignore_margin;
              if (Object.keys(own[k]!).every((x) => x === 'line_id' || own[k]![x] === undefined)) drop.add(k);
            }
            continue;
          }
          if (k < 0) {
            // an item-only change naming the recipe line; no qty, so the recipe's formula still applies
            own.push({ line_id: l.b.line_id });
            k = own.length - 1;
          }
          own[k]!.separate_pricing = next.sep;
          own[k]!.markup_pct = next.markup === null ? null : Number(next.markup);
          own[k]!.ignore_margin = next.ignore;
        }
        await updateRow('job_item', id, { custom_bom: own.filter((_, i) => !drop.has(i)) });
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------- the rows ----------
  const itemLinesOf = (g: ResolvedGroup, b: ResolvedBom): ItemLine[] => {
    if (g.kind === 'job') return [];
    const ids = Object.keys(b.shares);
    const prices = allocate(b.charged_cents, ids.map((id) => b.shares[id]!.cents));
    const out: ItemLine[] = [];
    ids.forEach((id, i) => {
      const sh = b.shares[id]!;
      const item = itemsById.get(id);
      if (!item) return;
      out.push({ key: `${b.key}|${id}`, g, b, item, cost: sh.cents, price: prices[i]!, zero: sh.cents === 0 && Number(sh.qty ?? 0) === 0 });
    });
    return out;
  };
  const modelOf = (key: string, line: MaterialLine, boms: { g: ResolvedGroup; b: ResolvedBom }[]): Model => {
    const priced = boms.filter((x) => !x.b.sub_work);
    return {
      key,
      line,
      boms,
      items: boms.flatMap((x) => itemLinesOf(x.g, x.b)).sort((a, b) => byNumber(a.item, b.item)),
      jobLines: boms.filter((x) => x.g.kind === 'job').map((x) => x.b),
      priceIn: priced.filter((x) => !x.b.outside_margin).reduce((n, x) => n + x.b.charged_cents, 0),
      priceOut: priced.filter((x) => x.b.outside_margin).reduce((n, x) => n + x.b.charged_cents, 0),
    };
  };
  const bomGroups = res.groups.filter((g) => g.bom.length > 0);
  // by material: the engine's one line per material across the job, with the BOM lines it gathers
  const pooled: Model[] = (() => {
    const lines = materialsByMaterial(bomGroups);
    const keys: string[] = [];
    const boms = new Map<string, { g: ResolvedGroup; b: ResolvedBom }[]>();
    for (const g of bomGroups)
      for (const b of g.bom) {
        const k = poolKey(b);
        if (!boms.has(k)) {
          boms.set(k, []);
          keys.push(k);
        }
        boms.get(k)!.push({ g, b });
      }
    return keys.map((k, i) => modelOf(k, lines[i]!, boms.get(k)!)).filter((m) => Number(m.line.qty) > 0 || m.line.warnings.length > 0);
  })();
  const shownLines = pooled.filter((m) => !m.line.sub_work);

  // ---------- the totals: the foot, and the head ----------
  const allBoms = bomGroups.flatMap((g) => g.bom).filter((b) => !b.sub_work);
  const sum = (bs: ResolvedBom[], f: (b: ResolvedBom) => number) => bs.reduce((n, b) => n + f(b), 0);
  const ins = allBoms.filter((b) => !b.outside_margin);
  const outs = allBoms.filter((b) => b.outside_margin);
  // the outside column's total is the engine's own (the Price tab's Materials priced outside margin row, to the cent); the
  // tax under each column is the engine's, summed exact and rounded once; the in-margin subtotal is the lines as charged
  const t = res.totals;
  const foot = {
    cost: t.material_cents,
    subIn: sum(ins, (b) => b.charged_cents),
    taxIn: t.tax_in_cents ?? sum(ins, (b) => b.tax_cents ?? 0),
    taxOut: t.tax_out_cents ?? sum(outs, (b) => b.tax_cents ?? 0),
    subOut: 0,
  };
  foot.subOut = (t.materials_out_charged_cents ?? sum(outs, (b) => b.charged_cents + (b.tax_cents ?? 0))) - foot.taxOut;
  const totIn = foot.subIn + foot.taxIn;
  const totOut = foot.subOut + foot.taxOut;
  const nOut = shownLines.filter((m) => m.line.outside_margin !== 'none').length;
  const nSep = shownLines.filter((m) => m.line.separate_pricing !== 'none').length;
  const lastPaid = (id: string | null) => (id ? inv.prices.get(id) : undefined);
  const nLp = shownLines.filter((m) => lastPaid(m.line.material_id)?.differs).length;
  const taxRate = pctText(res.pricing.sales_tax_pct ?? 0) || '0';

  const head = (
    <TabHead
      ctx={ctx}
      figure={(r) => money(r.totals.material_cents)}
      groups={[
        { key: 'material', label: 'By material' },
        { key: 'item', label: 'By kind of item' },
      ]}
      group={view}
      onGroup={setView}
      totals={
        <>
          <Tot value={<Money cents={foot.cost} />} label="materials cost, this build" />
          <Tot value={<Money cents={totIn + totOut} />} label="all materials" />
          <Tot sub value={nSep} label="separate pricing" />
          <Tot sub value={nOut} label="outside margin" />
          <Tot sub value={inv.loading ? '…' : nLp} label="last paid differs" />
        </>
      }
    />
  );
  if (bomGroups.length === 0)
    return (
      <div className="bd">
        {head}
        <div className="tab">
          <p className="none">No materials under this build yet. Add items and fill in their inputs.</p>
        </div>
      </div>
    );

  // ---------- the sheets, pooled across the job ----------
  const yieldPct = pctText(res.standards.sheet_yield_pct as string | number | null);
  const sheets = shownLines
    .filter((m) => m.line.material_id && lib.materials[m.line.material_id]?.sheet_length_in && Number(m.line.qty) > 0)
    .map((m) => {
      const parts = res.items.flatMap((i) => i.parts ?? []).filter((p) => p.material_id === m.line.material_id);
      return { m, parts: parts.reduce((n, p) => n + Number(p.qty), 0), sqft: parts.reduce((n, p) => n + Number(p.area_sq_in), 0) / 144 };
    });

  // ---------- one material row, and what opens under it ----------
  const usedBy = (m: Model) => {
    const nums = [...new Set(m.items.filter((x) => !x.zero).map((l) => l.item.number))];
    const words = nums.length > 3 ? [`${nums.length} items`] : nums;
    return [...words, ...(m.jobLines.length ? ['job'] : [])].join(', ') || '—';
  };
  const typedInput = (l: ItemLine): string | null => {
    const expr = recipeLine(l.item, l.b)?.unit_price_expr;
    if (!expr) return null;
    const keys = new Set(l.item.inputs.map((i) => i.key));
    return (expr.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []).find((w) => keys.has(w)) ?? null;
  };
  const itemJump = (l: ItemLine) => {
    const input = l.b.typed_price ? typedInput(l) : null;
    return input ? (
      <Jump ctx={ctx} home={`${l.item.number} · inputs`} full={`${l.item.number} › Inputs › ${l.item.inputs.find((i) => i.key === input)?.label ?? input}`} to={{ tab: 'item', item: l.item.id, lit: `input:${input}` }} back="materials" />
    ) : (
      <Jump ctx={ctx} home={`${l.item.number} · BOM`} full={`${l.item.number} › BOM › ${l.b.description}`} to={{ tab: 'item', item: l.item.id, lit: `line:${l.b.key}` }} back="materials" />
    );
  };
  const bomJump = (b: ResolvedBom) => <Jump ctx={ctx} home="Job setup · BOM" full={`Job setup › Job BOM › ${b.description}`} to={{ tab: 'setup', lit: `bom:${b.line_id ?? ''}` }} back="materials" />;
  const priceCells = (cents: number, out: boolean, sub = false) =>
    sub ? (
      <>
        <td className="r prc muted">—</td>
        <td className="r prc muted">—</td>
      </>
    ) : (
      <>
        <td className="r prc">{out ? null : <Money cents={cents} />}</td>
        <td className="r prc out">{out ? <Money cents={cents} /> : null}</td>
      </>
    );

  /** The three boxes on a row: Separate pricing, the markup, Ignore margin. `lines` are the item lines they set. */
  const boxes = (lines: ItemLine[], line: Pick<MaterialLine, 'separate_pricing' | 'outside_margin' | 'markup_pct'>, name: string): ReactNode => {
    if (lines.length === 0)
      return (
        <>
          <td className="c" />
          <td className="r" />
          <td className="c" />
        </>
      );
    const raws = lines.map((l) => pricingOf(l.item, l.b));
    const marks = new Set(raws.filter((p) => p.sep).map((p) => (p.markup === null ? 'std' : String(Number(p.markup)))));
    const sepAll = line.separate_pricing === 'all';
    const sepSome = line.separate_pricing === 'some';
    const off = ctx.locked || busy;
    return (
      <>
        <td className="c">
          <Box on={sepAll} mixed={sepSome} label={`Separate pricing, ${name}`} disabled={off} onClick={() => void write(lines, { sep: !sepAll })} />
        </td>
        <td className="r">
          {(sepAll || sepSome) && (
            <Markup
              used={line.markup_pct}
              typed={marks.size === 1 && !marks.has('std')}
              mixed={marks.size > 1 || line.markup_pct === 'mixed'}
              label={`Markup, ${name}`}
              disabled={off}
              onCommit={(v) => void write(lines.filter((_, i) => raws[i]!.sep || sepAll), { markup: v })}
            />
          )}
        </td>
        <td className="c">{sepAll && <Box on={line.outside_margin === 'all'} mixed={line.outside_margin === 'some'} label={`Ignore margin, ${name}`} disabled={off} onClick={() => void write(lines, { ignore: line.outside_margin !== 'all' })} />}</td>
      </>
    );
  };

  const materialRow = (m: Model, opensKey: string): ReactNode => {
    const l = m.line;
    const sub = l.sub_work;
    const leaves = m.items.filter((x) => !x.zero).length + m.jobLines.length;
    const singleJob = m.items.every((x) => x.zero) && m.jobLines.length === 1 ? m.jobLines[0]! : null;
    const opens = leaves > 0 && !singleJob;
    const open = opens && lineFolds.isOpen(opensKey);
    const lp = lastPaid(l.material_id);
    const first = m.boms[0]!;
    const out = l.outside_margin === 'all';
    return (
      <Fragment key={opensKey}>
        <tr className={`mat${opens ? ' hasitems' : ''}${open ? ' open' : ''}${Number(l.qty) === 0 ? ' zero' : ''}`} onClick={opens ? () => lineFolds.toggle(opensKey) : undefined}>
          <td className="cv">
            <span className={`chev${opens ? (open ? ' open' : '') : ' none'}`} />
          </td>
          <td className="nm" title={l.vendor ? `vendor: ${l.vendor}` : undefined}>
            {l.description}
            {m.boms.length === 1 && <Chain left expr={first.b.qty_expr} trace={first.b.trace} note={first.b.note} />}{' '}
            {sub && <Tag kind="off">sub work</Tag>} {!l.verified && l.material_id && Number(l.qty) > 0 && <UnverifiedTag items={res.items} to={m.boms.length === 1 ? groupAddress(first.g, first.b.line_id, jobTypeId) : materialAddress(res, l.material_id, jobTypeId)} title={`material: ${l.description}${l.groups > 1 ? `, from ${l.groups} kinds of item` : ''}`} />}
            {l.warnings.map((w) => (
              <div key={w} className="warn">
                {w}
              </div>
            ))}
            {seePrices && l.pack && <div className="sm">{bomPackWords(l.pack)}</div>}
            {l.links.length > 0 && (
              <div className="sm">
                {l.links.map((k) => (
                  <span key={`${k.item_id}|${k.url}`}>
                    {k.number}:{' '}
                    <a href={k.url} target="_blank" rel="noopener noreferrer" title={k.url} onClick={(e) => e.stopPropagation()}>
                      vendor page
                    </a>{' '}
                  </span>
                ))}
              </div>
            )}
            {l.material_id && Number(l.qty) > 0 && (tier === 'firm' || tier === 'committed') && (
              <div className="sm" onClick={(e) => e.stopPropagation()}>
                <Pull tier={tier} line={invLine(l.material_id)} allowed={canPull} />
              </div>
            )}
          </td>
          <td className="r">
            {qty(l.qty)} <small>{l.unit ?? ''}</small>
          </td>
          <td className="r" title={seePrices && l.pack ? bomPackWords(l.pack) : undefined}>
            {seePrices ? `$${l.price}` : '—'}
          </td>
          <td className="r">
            <Money cents={l.cents} />
          </td>
          <td className="src" title={lp?.differs && lp.last_paid ? `last paid ${money(Math.round(Number(lp.last_paid) * 100))} on ${lp.last_paid_at ?? ''}` : l.vendor ?? undefined}>
            {sub ? 'sub work · with the subs' : lp?.differs && lp.last_paid ? <span className="tag lp">last paid {money(Math.round(Number(lp.last_paid) * 100))}</span> : l.typed_price ? 'typed on the item' : 'catalog'}
          </td>
          {sub ? (
            <>
              <td className="c" />
              <td className="r" />
              <td className="c" />
            </>
          ) : (
            boxes(m.items, l, l.description)
          )}
          {sub ? (
            priceCells(0, false, true)
          ) : (
            <>
              <td className="r prc">{m.priceIn !== 0 || !out ? <Money cents={m.priceIn} /> : null}</td>
              <td className="r prc out">{m.priceOut !== 0 || out ? <Money cents={m.priceOut} /> : null}</td>
            </>
          )}
          <td className="ql">{usedBy(m)}</td>
          <td className="jc">{singleJob ? bomJump(singleJob) : l.material_id && libraryOpen ? <LibraryJump materialId={l.material_id} name={l.description} /> : null}</td>
        </tr>
        {open &&
          m.jobLines.map((b) => (
            <tr key={b.key} className="it">
              <td />
              <td className="nm">
                <span className="no">Job BOM</span>
                {b.note ? ` · ${b.note}` : ''}
              </td>
              <td className="r">{qty(b.qty)}</td>
              <td />
              <td className="r">
                <Money cents={b.cents} />
              </td>
              <td className="src">its line on Job setup</td>
              <td className="c" />
              <td className="r" />
              <td className="c" />
              {priceCells(b.charged_cents, b.outside_margin, b.sub_work)}
              <td />
              <td className="jc">{bomJump(b)}</td>
            </tr>
          ))}
        {open &&
          m.items.filter((x) => !x.zero).map((il) => (
            <tr key={il.key} className="it">
              <td />
              <td className="nm" title={il.b.pricing_own ? "This line's pricing is set on this item only" : undefined}>
                <span className="no">{il.item.number}</span> · {il.item.name}
              </td>
              <td className="r">{qty(il.b.shares[il.item.id]?.qty ?? '')}</td>
              <td />
              <td className="r">
                <Money cents={il.cost} />
              </td>
              <td />
              {sub ? (
                <>
                  <td className="c" />
                  <td className="r" />
                  <td className="c" />
                </>
              ) : (
                boxes([il], { separate_pricing: il.b.separate_pricing ? 'all' : 'none', outside_margin: il.b.outside_margin ? 'all' : 'none', markup_pct: il.b.markup_pct }, `${il.item.number}, ${l.description}`)
              )}
              {priceCells(il.price, il.b.outside_margin, sub)}
              <td />
              <td className="jc">{itemJump(il)}</td>
            </tr>
          ))}
      </Fragment>
    );
  };

  // ---------- the sections ----------
  const sectionOf = (m: Model) => (m.line.sub_work ? 'Sub work' : !m.line.material_id ? 'Typed prices' : lib.materials[m.line.material_id]?.category ?? 'No category');
  const rank = (name: string) => (name === 'Typed prices' ? 0 : name === 'Sub work' ? 3 : name === 'No category' ? 2 : 1);
  type Section = { key: string; name: string; models: Model[] };
  let sections: Section[];
  if (view === 'material') {
    const by = new Map<string, Model[]>();
    for (const m of pooled) by.set(sectionOf(m), [...(by.get(sectionOf(m)) ?? []), m]);
    sections = [...by].map(([name, ms]) => ({ key: name, name, models: ms.sort((a, b) => b.line.cents - a.line.cents) }));
    const cost = (s: Section) => s.models.reduce((n, m) => n + m.line.cents, 0);
    sections.sort((a, b) => rank(a.name) - rank(b.name) || cost(b) - cost(a) || a.name.localeCompare(b.name));
  } else {
    sections = [...bomGroups]
      .sort((a, b) => b.material_cents - a.material_cents)
      .map((g) => ({
        key: g.key,
        name: g.kind === 'job' ? 'Job BOM' : `${g.name}${g.recipe_name ? ` · ${g.recipe_name}` : ''}`,
        models: g.bom.filter((b) => Number(b.qty) > 0 || b.warnings.length > 0).map((b) => modelOf(b.key, materialsByMaterial([{ bom: [b] }])[0]!, [{ g, b }])),
      }))
      .filter((s) => s.models.length > 0);
  }

  return (
    <div className="bd">
      {head}
      <div className="tab">
        <div className="sheets">
          <h4>
            Sheets, pooled across the job <span className="who">yield standard {yieldPct ? `${yieldPct} %` : 'not set'} · set on Job setup</span>
          </h4>
          {sheets.length === 0 ? (
            <div className="none">None under this build: no item under it is cut from sheet stock{pooled.some((m) => m.line.typed_price) ? '; what it buys whole comes in as typed prices' : ''}.</div>
          ) : (
            <div className="bd-scroll">
              <table className="lt bd-sheets">
                <colgroup>
                  <col />
                  <col style={{ width: 96 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Sheet material</th>
                    <th className="r">Parts</th>
                    <th className="r">Area needed</th>
                    <th className="r">Sheets</th>
                    <th className="r">Yield</th>
                    <th className="r">Each</th>
                    <th className="r">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map(({ m, parts, sqft }) => (
                    <tr key={m.key} className="mat">
                      <td className="nm">{m.line.description}</td>
                      <td className="r">
                        {qty(parts)} {parts === 1 ? 'part' : 'parts'}
                      </td>
                      <td className="r">{sqft.toFixed(1)} sq ft</td>
                      <td className="r">{qty(m.line.qty)}</td>
                      <td className="r">{yieldPct ? `${yieldPct} %` : '—'}</td>
                      <td className="r">{seePrices ? `$${m.line.price}` : '—'}</td>
                      <td className="r">
                        <Money cents={m.line.cents} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Wide className="bd-scroll">
          <table className="lt bd-mats">
            <colgroup>
              <col style={{ width: 22 }} />
              <col />
              <col style={{ width: 70 }} />
              <col style={{ width: 66 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 72 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 102 }} />
              <col style={{ width: 124 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <th />
                <th>Material</th>
                <th className="r">Qty</th>
                <th className="r">Each</th>
                <th className="r">Cost</th>
                <th>Cost from</th>
                <th className="c">Separate pricing<Info>The boxes sit on each item's BOM line: a material's row sets every line of it at once and reads mixed when they differ; an item's row sets that line alone. A Job BOM line has no boxes. Separate pricing with Ignore margin prices the line at cost × (1 + its markup) + its tax, outside the gross margin.</Info></th>
                <th className="r">Markup</th>
                <th className="c">Ignore margin</th>
                <th className="r">Price</th>
                <th className="r">
                  Price<small>outside margin</small>
                </th>
                <th>Used by</th>
                <th className="r">Jump to</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => {
                const open = folds.isOpen(s.key);
                const cost = s.models.reduce((n, m) => n + m.line.cents, 0);
                const pin = s.models.filter((m) => !m.line.sub_work).reduce((n, m) => n + m.priceIn, 0);
                const pout = s.models.filter((m) => !m.line.sub_work).reduce((n, m) => n + m.priceOut, 0);
                return (
                  <Fragment key={s.key}>
                    <tr className="grp" onClick={() => folds.toggle(s.key)}>
                      <td />
                      <td colSpan={12}>
                        <span className={`chev${open ? ' open' : ''}`} />
                        {s.name}
                        <span className="ct">
                          {s.models.length} {s.models.length === 1 ? 'line' : 'lines'}
                        </span>{' '}
                        <span className="sum">
                          <Money cents={cost} />
                        </span>
                        {!open && seePrices && s.name !== 'Sub work' && (
                          <span className="ct">
                            price {money(pin)}
                            {pout ? ` · outside margin ${money(pout)}` : ''}
                          </span>
                        )}
                        {s.name === 'Sub work' && <span className="ct">priced with the subs, at the sub markup and no margin</span>}
                      </td>
                    </tr>
                    {open && s.models.map((m) => materialRow(m, `${view}:${m.key}`))}
                  </Fragment>
                );
              })}
            </tbody>
            {/* the foot's names are the Price grid's, for the same figures (F2, 2026-10-24): the shop's cost line reads
                "Cost to the shop", the two charged figures carry the grid's two row names, and only their sum is called
                Materials total; the explaining notes went (F4), the counts stay */}
            <tfoot>
              <tr className="tot2 first">
                <td />
                <td className="k" colSpan={3}>
                  Cost to the shop
                </td>
                <td className="r cst">
                  <Money cents={foot.cost} />
                </td>
                <td colSpan={4} />
                <td className="r">
                  <Money cents={foot.subIn} />
                </td>
                <td className="r">
                  <Money cents={foot.subOut} />
                </td>
                <td colSpan={2} className="note">
                  {shownLines.length - nOut} {shownLines.length - nOut === 1 ? 'line' : 'lines'} in margin · {nOut} {nOut === 1 ? 'line' : 'lines'} outside
                </td>
              </tr>
              <tr className="tot2">
                <td />
                <td className="k" colSpan={3}>
                  Sales tax {taxRate} %
                </td>
                <td />
                <td colSpan={4} />
                <td className="r">
                  <Money cents={foot.taxIn} />
                </td>
                <td className="r">
                  <Money cents={foot.taxOut} />
                </td>
                <td colSpan={2} className="note" />
              </tr>
              <tr className="tot2">
                <td />
                <td className="k" colSpan={3}>
                  Materials charged in margin
                </td>
                <td />
                <td colSpan={4} />
                <td className="r">
                  <Money cents={totIn} />
                </td>
                <td />
                <td colSpan={2} className="note" />
              </tr>
              <tr className="tot2">
                <td />
                <td className="k" colSpan={3}>
                  Materials priced outside margin
                </td>
                <td />
                <td colSpan={4} />
                <td />
                <td className="r">
                  <Money cents={totOut} />
                </td>
                <td colSpan={2} className="note" />
              </tr>
              <tr className="tot">
                <td />
                <td colSpan={3}>Materials total</td>
                <td />
                <td colSpan={4} />
                <td className="r" colSpan={2}>
                  <Money cents={totIn + totOut} />
                </td>
                <td colSpan={2} className="note" />
              </tr>
            </tfoot>
          </table>
        </Wide>
        <p className="only">
          {tier === 'firm'
            ? `This job has a locked revision, so its materials are ${names.firm}: they are on ${names.purchasing}, to buy.`
            : tier === 'committed'
              ? `This job is won but not locked yet, so its materials are ${names.committed}: they show grey on ${names.purchasing}, and a long-lead item can be pulled in now.`
              : tier === 'likely'
                ? `Materials reach ${names.purchasing} once the job is won. Until then they are only ${names.likely}: a forecast, under ${names.supply_demand}.`
                : `This job is not going ahead, so nothing of it reaches ${names.purchasing}.`}
        </p>
      </div>
    </div>
  );
}
